import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fmt } from '@/lib/format';
import { ordersApi } from '@/lib/apiServices';
import { PAYMENT_MODE_OPTIONS, PAYMENT_MESSAGES } from '../paymentCopy';

export default function OrderPaymentButton({ orderId, expiresAt }) {
  const [now, setNow] = useState(0);
  const [options, setOptions] = useState(null);
  const [selectedMode, setSelectedMode] = useState('');
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (!expiresAt) return undefined;
    const update = () => setNow(Date.now());
    const timeout = window.setTimeout(update, 0);
    const interval = window.setInterval(update, 1000);
    return () => {
      window.clearTimeout(timeout);
      window.clearInterval(interval);
    };
  }, [expiresAt]);
  const remainingSeconds = expiresAt && now
    ? Math.max(0, Math.ceil((new Date(expiresAt).getTime() - now) / 1000))
    : null;
  const availableOptions = (options ?? []).filter(
    (option) => option.available && !['OPTION_DISABLED', 'PAYMENT_NOT_REQUIRED'].includes(option.reason),
  );
  const openPayment = async () => {
    setLoadingOptions(true);
    setError('');
    try {
      const { data, error: apiError, response } = await ordersApi.paymentOptions(orderId);
      if (apiError || !data) {
        setError(response?.status === 409
          ? PAYMENT_MESSAGES.orderPaymentUnavailable
          : PAYMENT_MESSAGES.paymentOptionsError);
        return;
      }
      setOptions(data);
      const available = data.filter(
        (option) => option.available && !['OPTION_DISABLED', 'PAYMENT_NOT_REQUIRED'].includes(option.reason),
      );
      if (available.length === 1) {
        navigate(`/checkout/payment/${encodeURIComponent(orderId)}`, {
          state: { paymentMode: available[0].option, paymentOptions: data },
        });
      } else if (available.length === 0) {
        setError(PAYMENT_MESSAGES.noAvailablePaymentOption);
      }
    } catch (requestError) {
      console.error(requestError);
      setError(PAYMENT_MESSAGES.paymentOptionsError);
    } finally {
      setLoadingOptions(false);
    }
  };
  const continuePayment = () => {
    if (!selectedMode) return;
    navigate(`/checkout/payment/${encodeURIComponent(orderId)}`, {
      state: { paymentMode: selectedMode, paymentOptions: options },
    });
  };

  return (
    <div>
      {remainingSeconds != null && (
        <p className="order-card-meta">
          {remainingSeconds > 0
            ? `زمان باقی‌مانده برای پرداخت: ${fmt(Math.floor(remainingSeconds / 60))}:${fmt(remainingSeconds % 60).padStart(2, '۰')}`
            : 'مهلت پرداخت پایان یافته است.'}
        </p>
      )}
      <button
        className="btn btn-primary"
        disabled={loadingOptions}
        onClick={openPayment}
        type="button"
      >
        {loadingOptions ? PAYMENT_MESSAGES.paymentOptionsLoading : PAYMENT_MESSAGES.payOrder}
      </button>
      {error && <p className="orders-state orders-state--error" role="alert">{error}</p>}
      {availableOptions.length > 1 && (
        <div className="payment-methods">
          <fieldset>
            <legend>{PAYMENT_MESSAGES.methodHeading}</legend>
            {availableOptions.map((option) => {
              const method = PAYMENT_MODE_OPTIONS.find((item) => item.value === option.option);
              return (
                <label className="payment-method" key={option.option}>
                  <input
                    checked={selectedMode === option.option}
                    name={`payment-mode-${orderId}`}
                    onChange={() => setSelectedMode(option.option)}
                    type="radio"
                    value={option.option}
                  />
                  <span className="payment-method-copy">
                    <strong>{method?.label ?? option.option}</strong>
                    {method?.description && <span>{method.description}</span>}
                  </span>
                </label>
              );
            })}
          </fieldset>
          <button
            className="btn btn-primary"
            disabled={!selectedMode}
            onClick={continuePayment}
            type="button"
          >
            {PAYMENT_MESSAGES.continuePayment}
          </button>
        </div>
      )}
    </div>
  );
}
